<template>
  <div class="position-relative">
    <button
      ref="toggleDropdownButton"
      class="btn btn-light btn-sm dropdown-toggle filter"
      data-bs-toggle="dropdown"
      type="button"
    >
      <span v-html="filter.title"/>: {{ filter.selectedTitle }}
    </button>
    <div
      ref="dropdownMenu"
      class="dropdown-menu filter daterange"
    >
      <div class="px-3 pt-2">
        <div class="row gx-2">
          <div class="col">
            <input
              ref="dateFromInput"
              v-model="dateFrom"
              :class="['form-control form-control-sm date-range-input', firstRangePickerClass]"
              type="text"
            >
          </div>
          <div class="col col-tight separator">
            -
          </div>
          <div class="col">
            <input
              ref="dateToInput"
              v-model="dateTo"
              :class="['form-control form-control-sm date-range-input', secondRangePickerClass]"
              type="text"
            >
          </div>
        </div>
        <small
          v-if="noticeVisible"
          class="form-text text-muted"
        >
          {{ filter.notice }}
        </small>
        <hr>
        <div ref="rangePicker"/>
        <hr>
        <div class="d-flex buttons">
          <button
            v-if="filter.optional"
            class="btn btn-sm btn-info me-auto"
            @click.prevent="clearDateRange"
          >
            Clear
          </button>
          <button
            :class="['btn btn-sm btn-primary', {'ms-auto': filter.optional}]"
            type="button"
            @click.prevent="setDateRange"
          >
            Ok
          </button>
        </div>
        <div
          v-if="errorMessageVisible"
          class="alert alert-danger alert-sm mt-2 text-center"
        >
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import {computed, onBeforeUnmount, onMounted, ref, useTemplateRef} from 'vue'
import $ from 'jquery'
import {Dropdown} from 'bootstrap'
import type {GridFilter} from './types'
import {isNonEmptyString} from '../js-utils'

const model = defineModel<string[]>({required: true})

const props = defineProps({
  filter: {type: Object as () => GridFilter, required: true}
})

const rangePickerRef = useTemplateRef<HTMLElement>('rangePicker')
const dropdownMenuRef = useTemplateRef<HTMLElement>('dropdownMenu')
const dateFromInputRef = useTemplateRef<HTMLInputElement>('dateFromInput')
const dateToInputRef = useTemplateRef<HTMLInputElement>('dateToInput')
const toggleDropdownButtonRef = useTemplateRef<HTMLButtonElement>('toggleDropdownButton')

const activeRangePickerIdx = ref<number>(0)
const errorMessage = ref<string | null>(null)
const dateFrom = ref<string | null>(null)
const dateTo = ref<string | null>(null)

const firstRangePickerClass = computed((): string | null => {
  return activeRangePickerIdx.value != 1 ? 'border-primary' : null
})

const secondRangePickerClass = computed((): string | null => {
  return activeRangePickerIdx.value == 1 ? 'border-primary' : null
})

const errorMessageVisible = computed((): boolean => {
  return isNonEmptyString(errorMessage.value)
})

const noticeVisible = computed((): boolean => {
  return isNonEmptyString(props.filter.notice)
})

onMounted(() => {
  initDateRange()

  const rangePicker = rangePickerRef.value
  if (rangePicker) {
    $(rangePicker).datepicker({
      inline: true,
      format: 'dd/mm/yyyy',
      weekStart: 1,
      zIndex: 2048, // otherwise will be hidden behind a modal
    })
    $(rangePicker).on('pick.datepicker', (_e, _data) => {
      // when menu is open and user clicks on the second date field
      // a picked event is emitted, but date hasn't been actually picked
      if (rangePicker) {
        const pickedDate = $(rangePicker).datepicker('getDate', true)
        if (activeRangePickerIdx.value == 1) {
          dateTo.value = pickedDate
        } else {
          dateFrom.value = pickedDate
        }
      }
    })
  }

  toggleDropdownButtonRef.value?.addEventListener('show.bs.dropdown', () => {
    activeRangePickerIdx.value = 0
    initDateRange()
  })

  dropdownMenuRef.value?.addEventListener('click', (e): void => {
    // prevent closing menu if clicked on menu outside the controls
    e.stopPropagation()
  })

  dateFromInputRef.value?.addEventListener('focus', (): void => {
    activeRangePickerIdx.value = 0
    // set date for date picker
    if (dateFrom.value && rangePickerRef.value) {
      $(rangePickerRef.value).datepicker('setViewDate', dateFrom.value)
    }
  })

  dateToInputRef.value?.addEventListener('focus', (): void => {
    activeRangePickerIdx.value = 1
    if (dateTo.value && rangePickerRef.value) {
      $(rangePickerRef.value).datepicker('setViewDate', dateTo.value)
    }
  })
})

onBeforeUnmount(() => {
  if (toggleDropdownButtonRef.value) {
    Dropdown.getInstance(toggleDropdownButtonRef.value)?.dispose()
  }
})

const hideDropdown = (): void => {
  if (toggleDropdownButtonRef.value) {
    Dropdown.getOrCreateInstance(toggleDropdownButtonRef.value).hide()
  }
}

const initDateRange = (): void => {
  dateFrom.value = null
  dateTo.value = null
  errorMessage.value = null

  const sv = model.value
  if (Array.isArray(sv) && sv.length > 0) {
    dateFrom.value = sv[0]
    if (sv.length > 1) {
      dateTo.value = sv[1]
    }
  }
}

const setDateRange = (e: Event): void => {
  e.stopPropagation()
  if (props.filter.optional != true && !isNonEmptyString(dateFrom.value) && !isNonEmptyString(dateTo.value)) {
    errorMessage.value = 'Please select dates'
    return
  }

  let hasDates = false
  const dates = []
  if (isNonEmptyString(dateFrom.value)) {
    hasDates = true
    dates.push(dateFrom.value)
  } else {
    dates.push('')
  }

  if (isNonEmptyString(dateTo.value)) {
    hasDates = true
    dates.push(dateTo.value)
  } else {
    dates.push('')
  }

  if (!hasDates) {
    model.value = []
  } else {
    model.value = dates
  }

  hideDropdown()
}

const clearDateRange = (e: Event): void => {
  e.stopPropagation()

  model.value = []

  hideDropdown()
}
</script>
